export type VoiceCallState =
  | 'IDLE'
  | 'PREPARING_CALL'
  | 'PREPARING_BROWSER'
  | 'READY'
  | 'DIALING'
  | 'RINGING'
  | 'ANSWERED'
  | 'HELD'
  | 'ENDED'
  | 'FAILED'
  | 'BLOCKED';

export type VoicePhoneNumberDestination = {
  type: 'phone_number';
  e164Number: string;
};

export type VoiceExtensionDestination = {
  type: 'extension';
  extensionNumber: string;
};

export type VoiceDestination =
  VoicePhoneNumberDestination | VoiceExtensionDestination;

export interface VoiceCallSession {
  callSessionId: string | null;
  connectorId: string;
  clientUserRef: string | null;
  destination: VoiceDestination | null;
  /** External-number compatibility projection. Internal calls keep this null. */
  destinationE164: string | null;
  state: VoiceCallState;
  stateSequence: number | null;
  failureCategory: string | null;
  failureMessage: string | null;
  startedAt: string | null;
  lastEventAt: string | null;
  endpointDeviceId: string | null;
  endpointType: string | null;
  endpointSelectionReason: string | null;
  fromLineId?: string | null;
  contactId?: string | null;
  companyId?: string | null;
  dealId?: string | null;
  contactName?: string | null;
  companyName?: string | null;
  eventSchemaVersion?: string | null;
  direction?: VoiceCallDirection;
  fromNumber?: string | null;
  toNumber?: string | null;
  lineLabel?: string | null;
  callerPresentation?: string | null;
  /** Tenant-verified SparkOne employee name for an internal caller. */
  callerName?: string | null;
  routeTargetType?: string | null;
  routeTargetId?: string | null;
  routeSlot?: string | null;
  routeDecisionReason?: string | null;
  answeredAt?: string | null;
  endedAt?: string | null;
  durationSeconds?: number | null;
  notes?: string | null;
  disposition?: string | null;
  afterCallStatus?: VoiceAfterCallStatus;
  missedStatus?: VoiceMissedStatus | null;
  participants?: VoiceCallParticipant[];
  winningParticipantId?: string | null;
  winningEndpointDeviceId?: string | null;
  availableCommands?: string[];
  callListItemId?: string | null;
}

export type VoiceCallParticipant = {
  participantId: string;
  role: string;
  state: string;
  endpointDeviceId: string | null;
  endpointType?: string | null;
  handleUserRef: string | null;
  sipCorrelationId: string | null;
  terminalReason: string | null;
};

export interface VoiceConnectorOption {
  id: string;
  displayName: string;
  handleId: string;
  defaultLineId: string | null;
  clientUserRef: string | null;
}

interface VoiceCallPlacementBase {
  connectorId: string;
  clientUserRef: string;
  lineId: string | null;
  extensionId: string;
  destination: VoiceDestination;
  destinationE164?: string | null;
  idempotencyKey: string;
  endpointDeviceId: string;
  endpointType: string;
  endpointSelectionReason: string;
}

export interface VoiceOriginatedPlacement extends VoiceCallPlacementBase {
  phase: 'originated';
  callSessionId: string;
  crmPersistenceState: 'persisted' | 'pending';
  browserSession?: never;
}

export interface VoiceBrowserPreparedPlacement extends VoiceCallPlacementBase {
  phase: 'browser_session_prepared';
  callSessionId: null;
  endpointType: 'browser_webrtc';
  browserSession: VoiceBrowserSession;
}

export interface VoiceBrowserReadyPlacement extends VoiceCallPlacementBase {
  phase: 'browser_session_ready';
  callSessionId: null;
  endpointType: 'browser_webrtc';
  webrtcSessionId: string;
  browserSession?: never;
}

export type VoiceCallPlacement =
  | VoiceOriginatedPlacement
  | VoiceBrowserPreparedPlacement
  | VoiceBrowserReadyPlacement;

export type VoiceOriginateResult = {
  callSessionId: string;
  callerSipCorrelationId: string | null;
  state: string;
  crmPersistenceState: 'persisted' | 'pending';
  endpointDeviceId: string;
  endpointType: string;
  endpointSelectionReason: string;
};

export type VoiceCallDirection = 'INBOUND' | 'OUTBOUND' | 'INTERNAL';
export type VoiceAfterCallStatus = 'NOT_REQUIRED' | 'PENDING' | 'COMPLETED';
export type VoiceMissedStatus = 'NOT_MISSED' | 'UNRESOLVED' | 'RESOLVED';

type VoiceEndpointIdentity = {
  endpointDeviceId?: string | null;
  endpointType?: string | null;
  participants?: VoiceCallParticipant[];
  winningParticipantId?: string | null;
  winningEndpointDeviceId?: string | null;
};

type ResolvedVoiceEndpointIdentity = Required<
  Pick<VoiceEndpointIdentity, 'endpointDeviceId' | 'endpointType'>
>;

function canonicalWinningBrowserEndpoint(
  incoming: VoiceEndpointIdentity,
  currentClientUserRef: string | null | undefined,
): ResolvedVoiceEndpointIdentity | null {
  if (
    !currentClientUserRef ||
    !incoming.winningParticipantId ||
    !incoming.winningEndpointDeviceId
  )
    return null;
  const winner = incoming.participants?.find(
    (participant) =>
      participant.role === 'device_leg' &&
      participant.participantId === incoming.winningParticipantId &&
      participant.endpointDeviceId === incoming.winningEndpointDeviceId &&
      participant.endpointType === 'browser_webrtc' &&
      participant.handleUserRef === currentClientUserRef,
  );
  return winner
    ? {
        endpointDeviceId: incoming.winningEndpointDeviceId,
        endpointType: 'browser_webrtc',
      }
    : null;
}

export function reconcileVoiceEndpointIdentity(
  current: VoiceEndpointIdentity,
  incoming: VoiceEndpointIdentity,
  preserveCurrent: boolean,
  currentClientUserRef?: string | null,
): ResolvedVoiceEndpointIdentity {
  if (incoming.endpointDeviceId && incoming.endpointType) {
    return {
      endpointDeviceId: incoming.endpointDeviceId,
      endpointType: incoming.endpointType,
    };
  }
  const canonicalWinner = canonicalWinningBrowserEndpoint(
    incoming,
    currentClientUserRef,
  );
  if (canonicalWinner) return canonicalWinner;
  if (preserveCurrent && current.endpointDeviceId && current.endpointType) {
    return {
      endpointDeviceId: current.endpointDeviceId,
      endpointType: current.endpointType,
    };
  }
  return { endpointDeviceId: null, endpointType: null };
}

export function voiceEndpointLabel(
  endpointType: string | null | undefined,
): string {
  if (endpointType === 'browser_webrtc') return 'Browser phone';
  if (endpointType === 'hardphone' || endpointType === 'sip_device')
    return 'Desk phone';
  return 'Phone unavailable';
}

export type VoiceLine = {
  lineId: string;
  name: string;
  displayNumber: string;
  callerIdNumber?: string;
  status: string;
  isSelectableForOutbound: boolean;
  canOriginate: boolean;
  canReceive: boolean;
  isDefaultOutbound: boolean;
};

export type VoiceDialContext = {
  destination: VoiceDestination;
  contactId?: string | null;
  contactName?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  dealId?: string | null;
  callListItemId?: string | null;
  leaseToken?: string | null;
};

export type VoicePhonePolicy = {
  normalizedPhone: string;
  consentStatus: 'UNKNOWN' | 'CONSENTED' | 'NOT_CONSENTED' | 'DO_NOT_CALL';
  doNotCall: boolean;
  warningRequired: boolean;
  allowed: boolean;
  reasons: string[];
  reason?: string | null;
  notes?: string | null;
};

export type VoiceCallRecord = VoiceCallSession & {
  id: string;
  tenantId?: string;
  agentTenantMembershipId?: string | null;
  agentName?: string | null;
  callbackTaskId?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type VoiceCallListItem = {
  id: string;
  callListId: string;
  position: number;
  attemptCount?: number;
  state: 'PENDING' | 'LEASED' | 'IN_CALL' | 'COMPLETED' | 'SKIPPED';
  destinationE164: string;
  phoneNumber?: string;
  contactId?: string | null;
  contactName?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  dealId?: string | null;
  claimedByTenantMembershipId?: string | null;
  callSessionId?: string | null;
  disposition?: string | null;
  notes?: string | null;
  leaseToken?: string | null;
  leaseExpiresAt?: string | null;
};

export type VoiceCallList = {
  id: string;
  name: string;
  description?: string | null;
  state: 'DRAFT' | 'READY' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'ARCHIVED';
  lineId?: string | null;
  totalItems: number;
  completedItems: number;
  items?: VoiceCallListItem[];
  createdAt?: string;
  updatedAt?: string;
  _count?: { items: number };
};

export type VoiceRecordingArtifact = {
  id: string;
  providerArtifactId?: string | null;
  kind: 'RECORDING';
  status: 'PENDING' | 'AVAILABLE' | 'FAILED' | 'DELETED';
  mimeType?: string | null;
  durationSeconds?: number | null;
  playbackPath?: string | null;
};

export type VoiceTranscriptSegment = {
  id?: string;
  speaker?: string | null;
  startMs?: number | null;
  endMs?: number | null;
  text: string;
};

export type VoiceTranscriptArtifact = {
  id: string;
  providerArtifactId?: string | null;
  kind: 'TRANSCRIPT';
  status: 'PENDING' | 'AVAILABLE' | 'FAILED' | 'DELETED';
  transcriptPath?: string | null;
  text?: string | null;
  segments?: VoiceTranscriptSegment[];
};

export type VoiceArtifact = VoiceRecordingArtifact | VoiceTranscriptArtifact;

export type VoiceReportSummary = {
  totalCalls: number;
  answeredCalls: number;
  missedCalls: number;
  averageDurationSeconds: number;
  averageAfterCallSeconds: number;
  callbackCompletionRate: number;
  afterCallPending: number;
  afterCallCompleted: number;
  callbacks: number;
  agents: Array<{
    tenantMembershipId: string;
    name: string;
    calls: number;
    answered: number;
    missed: number;
    averageDurationSeconds: number;
  }>;
  lines: Array<{
    lineId: string;
    name: string;
    calls: number;
    answered: number;
    missed: number;
  }>;
};

export type VoiceBrowserMediaCredentials = {
  sessionId: string;
  iceServers: Array<{ urls: string[]; username?: string; credential?: string }>;
  issuedAt: string;
  expiresAt: string;
  ttlSeconds: number;
};

export interface VoiceBrowserSession {
  sessionId: string;
  iceServers: Array<{ urls: string[]; username?: string; credential?: string }>;
  mediaCredentialsExpiresAt: string | null;
  mediaCredentialsIssuedAt?: string | null;
  mediaCredentialsTtlSeconds?: number | null;
  signalingUrl: string;
  sipUri: string;
  sipUsername: string;
  sipSecret: string;
  registrationTtlSeconds: number;
  expiresAt: string;
}

/**
 * Authenticated app-shell bootstrap response for the long-lived Browser
 * endpoint. SIP/ICE credentials inside browserSession are memory-only.
 */
export interface VoicePersistentBrowserSession {
  connectorId: string;
  clientUserRef: string;
  lineId: string | null;
  extensionId: string;
  endpointDeviceId: string;
  browserSession: VoiceBrowserSession;
  renewalPredecessorSessionId?: string | null;
}

export type LocalSipRegistrationState =
  | 'idle'
  | 'connecting'
  | 'local_registered'
  | 'disconnected'
  | 'failed';

export type ProviderRegistrationState =
  | 'unknown'
  | 'issued'
  | 'registered'
  | 'retiring'
  | 'revoked'
  | 'expired'
  | 'failed';

export type BrowserRegistrationObservationOutcome =
  | 'verification_queued'
  | 'already_authoritative'
  | 'stale_identity'
  | 'unsupported'
  | 'rate_limited'
  | 'transient_failure';

export type BrowserRegistrationObservationResult = {
  accepted: boolean;
  outcome: BrowserRegistrationObservationOutcome;
};

export type VoiceBrowserSessionStatus =
  | 'issued'
  | 'registered'
  | 'revocation_pending'
  | 'retiring'
  | 'expired'
  | 'revoked'
  | 'failed';

export type VoiceBrowserSessionRegistration = {
  sessionId: string;
  status: VoiceBrowserSessionStatus;
  lastRegisteredAt: string | null;
};

export type VoiceBrowserInstallationLifecycle =
  | 'PREPARING'
  | 'REGISTERING'
  | 'READY'
  | 'ROTATING'
  | 'DEGRADED'
  | 'REVOKING'
  | 'REVOKED'
  | 'DISABLED'
  | 'FAILED';

export type VoiceBrowserInstallationStatus =
  | 'issued'
  | 'registered'
  | 'retiring'
  | 'revoked'
  | 'expired'
  | 'failed';

export type VoiceBrowserInstallationSessionProjection = {
  generation: number;
  sessionId: string;
  status: VoiceBrowserInstallationStatus;
  expiresAt: string;
};

export type VoiceBrowserActivationProof = {
  leaseId: string;
  tabId: string;
  reclaimToken: string;
};

export type VoiceBrowserActivationLease = {
  leaseId: string;
  state: 'ACTIVE' | 'RELEASED';
  issuedAt: string;
  expiresAt: string;
  ttlSeconds: number;
};

export type VoiceBrowserInstallationEnsureNextAction =
  | 'none'
  | 'wait'
  | 'retry_ensure';

export type VoiceBrowserInstallationEnsureDisposition = {
  nextAction: VoiceBrowserInstallationEnsureNextAction;
  reason: string | null;
  retryAfterMs: number | null;
};

/** Secret-free authoritative projection used by recovery reads. */
export interface VoiceBrowserInstallationProjection {
  installationId: string;
  browserDeviceInstanceId: string;
  activationId: string | null;
  activationLease: VoiceBrowserActivationLease | null;
  connectorId: string;
  clientUserRef: string;
  lineId: string | null;
  extensionId: string | null;
  endpointDeviceId: string | null;
  lifecycleState: VoiceBrowserInstallationLifecycle;
  generation: number;
  sessionId: string;
  status: VoiceBrowserInstallationStatus;
  expiresAt: string;
  stateVersion: number;
  /** Monotonic fence for asynchronous ensure preparation episodes. */
  preparationGeneration: number;
  pending: VoiceBrowserInstallationSessionProjection | null;
  /**
   * Null is retained while polling an older backend projection. A preparation
   * recovery episode interprets null as wait, never as retry authorization.
   */
  ensureDisposition?: VoiceBrowserInstallationEnsureDisposition | null;
  lastRegisteredAt: string | null;
  lastProviderEventId: string | null;
  lastProviderEventAt: string | null;
}

export type VoiceBrowserInstallationEnsureReady =
  VoiceBrowserInstallationProjection & {
    outcome: 'ready';
    /** Credentials are absent only under the existing safe-reclaim contract. */
    browserSession: VoiceBrowserSession | null;
  };

export type VoiceBrowserInstallationPreparingReason =
  | 'preparation_lease_active'
  | 'current_session_retiring'
  | 'pending_successor_converging'
  | 'provider_projection_pending';

export type VoiceBrowserInstallationPreparingTransition = {
  generation: number;
  sessionId: string;
  status: string;
};

export type VoiceBrowserInstallationPreparing = {
  outcome: 'preparing';
  installationId: string;
  stateVersion: number;
  preparationGeneration: number;
  reason: VoiceBrowserInstallationPreparingReason;
  nextAction: 'wait';
  retryAfterMs: number;
  current?: VoiceBrowserInstallationPreparingTransition;
  pending?: VoiceBrowserInstallationPreparingTransition;
};

/** Ensure response; credentials are present only for a newly delivered generation. */
export type VoiceBrowserInstallationEnsure =
  | VoiceBrowserInstallationEnsureReady
  | VoiceBrowserInstallationPreparing;

export const isBrowserPlacement = (
  placement: VoiceCallPlacement,
): placement is VoiceBrowserPreparedPlacement =>
  placement.phase === 'browser_session_prepared' &&
  placement.endpointType === 'browser_webrtc' &&
  Boolean(placement.browserSession);

export const isReadyBrowserPlacement = (
  placement: VoiceCallPlacement,
): placement is VoiceBrowserReadyPlacement =>
  placement.phase === 'browser_session_ready' &&
  placement.endpointType === 'browser_webrtc' &&
  Boolean(placement.webrtcSessionId);

export const isSipDevicePlacement = (
  placement: VoiceCallPlacement,
): placement is VoiceOriginatedPlacement =>
  placement.phase === 'originated' &&
  (placement.endpointType === 'hardphone' ||
    placement.endpointType === 'sip_device');

export const isE164Voice = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /^\+[1-9]\d{7,14}$/.test(trimmed);
};

export const isVoiceExtension = (value: string): boolean =>
  /^\d{2,12}$/.test(value.trim());

export const voiceDestinationValue = (destination: VoiceDestination): string =>
  destination.type === 'phone_number'
    ? destination.e164Number
    : destination.extensionNumber;

export const voiceDestinationLabel = (
  destination: VoiceDestination | null | undefined,
  destinationE164?: string | null,
): string => {
  if (destination?.type === 'extension')
    return `Extension ${destination.extensionNumber}`;
  if (destination?.type === 'phone_number') return destination.e164Number;
  return destinationE164?.trim() || '—';
};

export const isVoiceDestinationValid = (
  destination: VoiceDestination,
): boolean =>
  destination.type === 'phone_number'
    ? isE164Voice(destination.e164Number)
    : isVoiceExtension(destination.extensionNumber);

/**
 * Canonicalize CRM phone values before they enter the shared dialer. This
 * mirrors SparkOne's server-side international matching rules for common CRM
 * formatting while leaving an ambiguous/invalid value visible for review.
 */
export const normalizeVoiceDestination = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  // Do not turn arbitrary CRM text into a different valid-looking number.
  // Extensions, labels, multiple plus signs, slashes, and unmatched grouping
  // remain visible for operator review and therefore fail isE164Voice().
  if (!/^[+\d\s().-]+$/.test(trimmed)) return trimmed;
  let depth = 0;
  let groupHasDigit = false;
  for (const character of trimmed) {
    if (character === '(') {
      if (depth !== 0) return trimmed;
      depth = 1;
      groupHasDigit = false;
    } else if (character === ')') {
      if (depth !== 1 || !groupHasDigit) return trimmed;
      depth = 0;
    } else if (depth === 1 && /\d/.test(character)) {
      groupHasDigit = true;
    }
  }
  if (depth !== 0) return trimmed;

  const compact = trimmed.replace(/[\s().-]/g, '');
  const international = compact.startsWith('+')
    ? compact
    : compact.startsWith('00')
      ? `+${compact.slice(2)}`
      : null;
  if (!international || !/^\+[1-9]\d{7,14}$/.test(international)) {
    return trimmed;
  }
  return international;
};
